import { useCallback, useEffect, useRef, useState } from 'react';
import { useReactQuery } from '../api';
import type { components } from '../api/__generated__/openapi';
import { useIdentityContext } from '../components/IdentityProvider';
import {
  clearUserDataCache,
  userCacheManager,
} from '../utils/userCacheManager';
import { userStorage } from '../utils/userStorage';

type WhoisUser = components['schemas']['WhoisUser'];
type WhoisV1ApiResponse = components['schemas']['WhoisV1ApiResponse'];

// Cache key for current user data (legacy support)
const USER_DATA_CACHE_KEY = 'flik_user_data';
const CACHE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

interface CachedUserData {
  data: any;
  timestamp: number;
}

/**
 * Enhanced hook for user data that supports caching multiple users by identity.
 * This hook ensures only one whoami query is made across the entire application
 * and provides caching for other users' data to avoid unnecessary API calls.
 *
 * This hook implements local caching to provide immediate user data
 * on page reload, improving user experience.
 */
export function useUserData() {
  const { isLoggedIn } = useIdentityContext();
  const apiClient = useReactQuery();
  const [cachedUserData, setCachedUserData] = useState<any>(null);
  const [isCacheValid, setIsCacheValid] = useState(false);

  // Load cached data on mount
  useEffect(() => {
    if (isLoggedIn) {
      const cached = userStorage.get<CachedUserData>(USER_DATA_CACHE_KEY);
      if (
        cached &&
        cached.timestamp &&
        Date.now() - cached.timestamp < CACHE_EXPIRY_MS
      ) {
        setCachedUserData(cached.data);
        setIsCacheValid(true);
      }
    }
  }, [isLoggedIn]);

  // Single whoami query for the entire app
  const whoamiQuery = apiClient.useQuery(
    'get',
    '/v1/whoami',
    {},
    {
      enabled: isLoggedIn,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );

  // Update cache when fresh data is received
  useEffect(() => {
    if (whoamiQuery.data && isLoggedIn) {
      const cacheData: CachedUserData = {
        data: whoamiQuery.data,
        timestamp: Date.now(),
      };
      userStorage.set(USER_DATA_CACHE_KEY, cacheData);
      setCachedUserData(whoamiQuery.data);
      setIsCacheValid(true);
    }
  }, [whoamiQuery.data, isLoggedIn]);

  // Use fresh data if available, otherwise fall back to cached data
  const whoamiData = whoamiQuery.data || cachedUserData;
  const userData = whoamiData?.user;
  const identityData = whoamiData?.identity;

  // Generate initials from first and last name
  const getInitials = (firstName?: string, lastName?: string) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0).toUpperCase()}${lastName
        .charAt(0)
        .toUpperCase()}`;
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }
    return 'U'; // Default to 'U' for User
  };

  const initials = userData
    ? getInitials(
        userData.first_name || undefined,
        userData.last_name || undefined,
      )
    : 'U';

  // Determine loading state - only show loading if we have no cached data and query is loading
  const isLoading = whoamiQuery.isLoading && !isCacheValid;

  // Create mutation for whois API
  const whoisMutation = apiClient.useMutation('post', '/v1/whois');

  /**
   * Get user data by identity from cache or fetch if not cached
   */
  const getUserDataByIdentity = useCallback(
    async (identity: string) => {
      // Check if we have valid cached data
      if (userCacheManager.hasValidUserData(identity)) {
        return userCacheManager.getUserData(identity);
      }

      // Fetch user data using whois API
      try {
        const response = (await whoisMutation.mutateAsync({
          body: {
            identityIds: [identity],
          },
        })) as WhoisV1ApiResponse;

        if (response?.users && response.users.length > 0) {
          const whoisUser = response.users[0];
          if (whoisUser && whoisUser.identity) {
            const userData = {
              identity: whoisUser.identity,
              user: whoisUser.user,
            };
            userCacheManager.setUserData(identity, userData);
            return userData;
          }
        }
        return null;
      } catch (error) {
        console.error(
          'Failed to fetch user data for identity:',
          identity,
          error,
        );
        return null;
      }
    },
    [whoisMutation],
  );

  /**
   * Get multiple users' data by identities, using cache when available
   */
  const getMultipleUsersData = useCallback(
    async (identities: string[]) => {
      const results: { [identity: string]: any } = {};
      const identitiesToFetch: string[] = [];

      // Check cache first
      identities.forEach((identity) => {
        if (userCacheManager.hasValidUserData(identity)) {
          results[identity] = userCacheManager.getUserData(identity);
        } else {
          identitiesToFetch.push(identity);
        }
      });

      // Fetch uncached identities in batch
      if (identitiesToFetch.length > 0) {
        try {
          const response = (await whoisMutation.mutateAsync({
            body: {
              identityIds: identitiesToFetch,
            },
          })) as WhoisV1ApiResponse;

          if (response?.users) {
            const userDataMap = new Map(
              response.users
                .filter(
                  (whoisUser): whoisUser is WhoisUser =>
                    whoisUser !== null && whoisUser.identity !== undefined,
                )
                .map((whoisUser) => [
                  whoisUser.identity.id,
                  {
                    identity: whoisUser.identity,
                    user: whoisUser.user,
                  },
                ]),
            );

            identitiesToFetch.forEach((identity) => {
              const userData = userDataMap.get(identity);
              if (userData) {
                results[identity] = userData;
                userCacheManager.setUserData(identity, userData);
              }
            });
          }
        } catch (error) {
          console.error('Failed to fetch multiple users data:', error);
        }
      }

      return results;
    },
    [whoisMutation],
  );

  /**
   * Get user display name by identity
   */
  const getUserDisplayName = useCallback((identity: string): string => {
    const userData = userCacheManager.getUserData(identity);
    if (userData) {
      const { user, identity: identityData } = userData;
      if (user?.first_name && user?.last_name) {
        return `${user.first_name} ${user.last_name}`.trim();
      }
      if (user?.first_name) return user.first_name;
      if (user?.last_name) return user.last_name;
      if (identityData?.email) return identityData.email;
    }
    return identity; // Fallback to identity
  }, []);

  /**
   * Get user initials by identity
   */
  const getUserInitials = useCallback((identity: string): string => {
    const userData = userCacheManager.getUserData(identity);
    if (userData) {
      const { user } = userData;
      if (user?.first_name && user?.last_name) {
        return `${user.first_name.charAt(0)}${user.last_name.charAt(
          0,
        )}`.toUpperCase();
      }
      if (user?.first_name) return user.first_name.charAt(0).toUpperCase();
      if (user?.last_name) return user.last_name.charAt(0).toUpperCase();
    }
    return 'U'; // Default fallback
  }, []);

  /**
   * Check if user data is cached for an identity
   */
  const isUserDataCached = useCallback((identity: string): boolean => {
    return userCacheManager.hasValidUserData(identity);
  }, []);

  /**
   * Preload user data for multiple identities
   */
  const preloadUserData = useCallback(
    async (identities: string[]) => {
      return getMultipleUsersData(identities);
    },
    [getMultipleUsersData],
  );

  // Ref to store the debounce timeout
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Debounced version of getMultipleUsersData to prevent rapid-fire API calls
   */
  const debouncedGetMultipleUsersData = useCallback(
    (identities: string[], delay: number = 300) => {
      return new Promise<{ [identity: string]: any }>((resolve) => {
        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // Set new timeout
        debounceTimeoutRef.current = setTimeout(async () => {
          try {
            const results = await getMultipleUsersData(identities);
            resolve(results);
          } catch (error) {
            console.error('Debounced getMultipleUsersData failed:', error);
            resolve({});
          }
        }, delay);
      });
    },
    [getMultipleUsersData],
  );

  return {
    // Current user data (existing functionality)
    userData: userData,
    whoamiData: whoamiData,
    identityData: identityData,
    isLoading: isLoading,
    isError: whoamiQuery.isError,
    error: whoamiQuery.error,
    firstName: userData?.first_name,
    lastName: userData?.last_name,
    email: identityData?.email,
    displayName: userData
      ? `${userData.first_name || ''} ${userData.last_name || ''}`.trim() ||
        identityData?.email
      : undefined,
    initials,

    // Enhanced functionality for other users
    getUserDataByIdentity,
    getMultipleUsersData,
    getUserDisplayName,
    getUserInitials,
    isUserDataCached,
    preloadUserData,
    debouncedGetMultipleUsersData,
  };
}

// Re-export clearUserDataCache for backward compatibility
export { clearUserDataCache };
